/**
 * ── SLA snooze ───────────────────────────────────────────────────────────────
 *
 * Park an over-SLA candidate WITH A REASON, so the flag is muted deliberately
 * rather than lost. Board-owned (see proxy lib/taSlaSnooze.js), keyed by the
 * Lever opportunity. The board is per-recruiter, so every call passes the
 * boardEmail whose card is being parked, and the proxy drops that board's cache
 * so the change shows on the next load.
 *
 * Extracted from taActionBoardApi (at its line budget) and re-exported there, so
 * `import * as api` keeps reaching these.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

export interface SlaSnooze {
  opportunityId: string;
  snoozedUntil: string;
  reason: string;
  tags: string[];
  by: string;
}

export interface SnoozeInput {
  opportunityId: string;
  boardEmail: string;
  /** ISO timestamp — computed on the client in Europe/Berlin (see snoozePresets). */
  snoozedUntil: string;
  /** Free-text reason; combined with any tags server-side. */
  reason?: string;
  /** Quick-pick reason chips. */
  tags?: string[];
}

/**
 * Snooze a candidate until `snoozedUntil` with a mandatory reason.
 *
 * Analytics carry NO candidate data — only how far out the snooze is (a coarse
 * bucket) and whether a free-text reason and/or tags were used.
 */
export async function snoozeSlaCandidate(input: SnoozeInput): Promise<SlaSnooze> {
  try {
    const res = await authenticatedFetch(`${BASE}/sla-snooze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = (await res.json()) as { snooze: SlaSnooze };
    void logAnalyticsEvent('sla_snooze_set', {
      days_bucket: snoozeDaysBucket(input.snoozedUntil),
      with_free_text: Boolean(input.reason && input.reason.trim()),
      tag_count: input.tags?.length ?? 0,
    });
    return data.snooze;
  } catch (error) {
    void logAnalyticsEvent('sla_snooze_set_failed', {
      kind: error instanceof Error ? error.name : typeof error,
    });
    throw error;
  }
}

/** Lift a snooze. Idempotent server-side — releasing an already-woken card is fine. */
export async function releaseSlaSnooze(opportunityId: string, boardEmail: string): Promise<void> {
  try {
    await authenticatedFetch(`${BASE}/sla-snooze/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ opportunityId, boardEmail }),
    });
    void logAnalyticsEvent('sla_snooze_released', {});
  } catch (error) {
    void logAnalyticsEvent('sla_snooze_released_failed', {
      kind: error instanceof Error ? error.name : typeof error,
    });
    throw error;
  }
}

/** Coarse bucket of how far out a snooze is — for analytics, never the raw date. */
function snoozeDaysBucket(iso: string): string {
  const days = (Date.parse(iso) - Date.now()) / 86_400_000;
  if (!Number.isFinite(days)) return 'unknown';
  if (days <= 3) return '<=3d';
  if (days <= 7) return '<=1w';
  if (days <= 14) return '<=2w';
  return '>2w';
}
